class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('change', this.handleProtectionSwitchChange.bind(this));
  }

  /*
    An inline style keeps .drawer at display:none until component-cart-drawer.css
    (loaded async) has applied; showing it earlier puts an unstyled drawer in
    the page flow. This used to wait for window.load, which waits for every
    image and app script, so on slow loads the first cart tap did nothing.
    Show it as soon as the drawer's own stylesheet is in effect instead.
  */
  connectedCallback() {
    const show = () => { this.style.display = 'flex'; };
    const styled = () => getComputedStyle(this).position === 'fixed';
    if (styled()) return show();
    const poll = setInterval(() => {
      if (styled()) { clearInterval(poll); show(); }
    }, 100);
    window.addEventListener('load', () => { clearInterval(poll); show(); }, { once: true });
  }

  async handleProtectionSwitchChange(e) {
    const switchInput = document.querySelector('.footer-upsell__price-switch-input');
    if (!switchInput) return;

    if (e.target !== switchInput) return;

    const switchLabel = switchInput.closest('.footer-upsell__price-switch');
    if (!switchLabel) return;

    switchLabel.classList.toggle('loading');

    if (switchInput.checked) {
      await this.addProtectedToCart(e);
    } else {
      await this.removeProtectedFromCart(e);
    }

    switchLabel.classList.remove('loading');
  }

  async addProtectedToCart() {
    const label = this.querySelector('.footer-upsell__price-switch[data-variant-id]');
    const btn = label.querySelector('.footer-upsell__price-switch-input');
    if (!btn || btn.disabled) return;

    const variantId = label.dataset.variantId;
    label.setAttribute('aria-disabled', 'true');
    label.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map(s => s.section));
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    try {
      const res = await fetch(routes.cart_add_url, config);
      const response = await res.json();

      if (response.status) {
        console.warn('[CartDrawer] add to cart error:', response.description);
        return;
      }

      label.classList.add('active');

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);

        if (!elementToReplace) return;
        elementToReplace.innerHTML = this.getSectionInnerHTML(response.sections[section.section], section.selector);
      });

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-footer',
        productVariantId: variantId,
        cartData: response,
      });
    } catch (err) {
      console.error('[CartDrawer]', err);
    } finally {
      label.classList.remove('loading');
      label.removeAttribute('aria-disabled');
    }
  }

  async removeProtectedFromCart(e) {
    const label = this.querySelector('.footer-upsell__price-switch[data-variant-id]');
    const variantId = label.dataset.variantId;
    const line = this.querySelector(`.cart-items cart-remove-button[data-variant-id="${variantId}"]`)?.dataset.index;
    const switchLabel = this.querySelector('.footer-upsell__price-switch');

    switchLabel.classList.add('loading');

    const cartItems = this.querySelector('cart-items') || this.querySelector('cart-drawer-items');
    cartItems.updateQuantity(line, 0, e);

    switchLabel.classList.remove('loading');
    switchLabel.classList.remove('active');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Guard: PartBot's /cart/update.js can overlap /cart/add, making Shopify return sections: null.
    // Re-fetch the drawer sections instead of crashing and leaving the drawer shut.
    if (!parsedState || !parsedState.sections) {
      fetch(`${routes.cart_url}?sections=cart-drawer,cart-icon-bubble`)
        .then((response) => response.json())
        .then((sections) => this.renderContents(Object.assign({}, parsedState, { sections })))
        .catch(() => this.open());
      return;
    }
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.getElementById(section.id)?.querySelector(section.selector) ||
        document.getElementById(section.id)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerRecommendation extends HTMLElement {
  connectedCallback() {
    this.container = this.querySelector('.cart-drawer__track');
    this.slider = this.querySelector('slider-component');
    this.cart = document.querySelector('cart-drawer');

    this.#loadWhenNeeded();
    this.addEventListener('click', this.handleAddToCart.bind(this));
    this.addEventListener('change', this.handleVariantChange.bind(this));
  }

  disconnectedCallback() {
    this.#stopWaiting?.();
  }

  /*
    The recommendations used to be fetched on every page view, even with the
    drawer closed. Wait until the drawer opens, or warm up as soon as the
    shopper reaches for the cart icon (hover, touch or keyboard focus), so the
    row is usually filled by the time the drawer slides in.
  */
  #stopWaiting = null;
  #loadWhenNeeded() {
    if (!this.cart || this.cart.classList.contains('active')) {
      this.load();
      return;
    }
    const icon = document.getElementById('cart-icon-bubble');
    const intents = ['pointerenter', 'touchstart', 'focusin'];
    const observer = new MutationObserver(() => {
      if (this.cart.classList.contains('active')) go();
    });
    const stop = () => {
      observer.disconnect();
      if (icon) intents.forEach((type) => icon.removeEventListener(type, go));
      this.#stopWaiting = null;
    };
    const go = () => {
      stop();
      this.load();
    };
    observer.observe(this.cart, { attributes: true, attributeFilter: ['class'] });
    if (icon) intents.forEach((type) => icon.addEventListener(type, go, { passive: true }));
    this.#stopWaiting = stop;
  }

  /*
    sections/cart-recs.liquid merges the bought together lists of everything in
    the cart. The drawer is rebuilt on every cart change, which lands here again.
  */
  async load() {
    if (!this.container) return;

    try {
      const res = await fetch(`${routes.cart_url}?section_id=cart-recs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const products = JSON.parse(doc.querySelector('[data-cart-recs]')?.textContent || '[]');
      if (!products.length) return;

      this.container.innerHTML = products
        .map((p, i) => `<li id="Slide-CartDrawerRecs-${i + 1}" class="cart-drawer__slide">${this.#renderCard(p)}</li>`)
        .join('');

      // The native slider-component reads its slides when it is created, so
      // tell it about the ones that just arrived (arrows, page counts).
      this.slider?.resetPages?.();
      this.#setupDots();
    } catch (e) {
      console.warn('[CartDrawerRecommendation] fetch failed:', e);
    }
  }

  /*
    Swipe row: cards are narrower than the track so the next one peeks in, and
    a row of dots (one per visible card, clickable for mouse users) replaces
    the old prev/next arrows. constants.js hides non-fitting cards after this
    runs, so the dots are rebuilt whenever a slide's class changes.
  */
  #setupDots() {
    if (!this.container) return;
    if (!this.dots) {
      this.dots = document.createElement('div');
      this.dots.className = 'cart-drawer__dots';
      this.container.after(this.dots);
      this.dots.addEventListener('click', (e) => {
        const dot = e.target.closest('button[data-index]');
        if (!dot) return;
        const slide = this.#visibleSlides()[Number(dot.dataset.index)];
        if (slide) this.container.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      });
      this.container.addEventListener('scroll', () => {
        if (this.dotFrame) return;
        this.dotFrame = requestAnimationFrame(() => {
          this.dotFrame = null;
          this.#markDot();
        });
      }, { passive: true });
      new MutationObserver(() => this.#renderDots()).observe(this.container, {
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      this.#setupMouseScroll();
    }
    this.#renderDots();
  }

  /*
    Mouse users can't swipe: let them drag the row, and clicking the card that
    peeks in from the right slides it into view (instead of opening it).
    Touch keeps native scrolling.
  */
  #setupMouseScroll() {
    const track = this.container;
    let startX = 0;
    let startLeft = 0;
    let dragging = false;
    let moved = false;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('select, button, input')) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
    });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;
      if (!moved) {
        moved = true;
        track.classList.add('is-dragging');
      }
      track.scrollLeft = startLeft - dx;
    });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      if (!moved) return;
      track.classList.remove('is-dragging');
      const slides = this.#visibleSlides();
      const left = track.scrollLeft;
      const nearest = slides.reduce((best, s) =>
        Math.abs(s.offsetLeft - left) < Math.abs(best.offsetLeft - left) ? s : best, slides[0]);
      if (nearest) track.scrollTo({ left: nearest.offsetLeft, behavior: 'smooth' });
    });

    // A tap that lands while the row is still moving (or just stopped) is the
    // end of a swipe, not a choice: on phones it was opening product pages.
    let lastScrollAt = 0;
    track.addEventListener('scroll', () => { lastScrollAt = Date.now(); }, { passive: true });

    // Capture phase, so it runs before the card's own link and Add handlers.
    track.addEventListener('click', (e) => {
      if (moved || Date.now() - lastScrollAt < 400) {
        e.preventDefault();
        e.stopPropagation();
        moved = false;
        return;
      }
      const slide = e.target.closest('.cart-drawer__slide');
      if (!slide) return;
      const t = track.getBoundingClientRect();
      const r = slide.getBoundingClientRect();
      if (r.right > t.right + 2 || r.left < t.left - 2) {
        e.preventDefault();
        e.stopPropagation();
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      }
    }, true);
  }

  #visibleSlides() {
    return Array.from(this.container.children).filter((s) => !s.classList.contains('cvf-rec-hidden'));
  }

  #renderDots() {
    const count = this.#visibleSlides().length;
    if (this.dots.childElementCount === count) return this.#markDot();
    this.dots.hidden = count < 2;
    this.dots.innerHTML = Array.from({ length: count }, (_, i) =>
      `<button type="button" data-index="${i}" aria-label="Show recommendation ${i + 1} of ${count}"></button>`
    ).join('');
    this.#markDot();
  }

  #markDot() {
    const slides = this.#visibleSlides();
    if (!slides.length || !this.dots) return;
    const left = this.container.scrollLeft;
    let active = 0;
    slides.forEach((s, i) => {
      if (Math.abs(s.offsetLeft - left) < Math.abs(slides[active].offsetLeft - left)) active = i;
    });
    Array.from(this.dots.children).forEach((d, i) => d.toggleAttribute('aria-current', i === active));
  }

  #formatPrice(cents) {
    return window.Shopify?.formatMoney
      ? Shopify.formatMoney(cents)
      : (cents / 100).toFixed(2);
  }

  #hasMultipleVariants(product) {
    return !(product.variants.length === 1 && product.variants[0].title === 'Default Title');
  }

  #renderVariantSelect(product) {
    if (!this.#hasMultipleVariants(product)) return '';

    const options = product.variants.map((v, i) => `
      <option 
        value="${v.id}" 
        data-price="${v.price}"
        ${!v.available ? 'disabled' : ''}
      >
        ${v.title}${!v.available ? ' - Sold out' : ''}
      </option>
    `).join('');

    return `<select class="recommendations-card__variants select__select" data-product-id="${product.id}" data-is-upsell="true" aria-label="Choose an option for ${product.title}">${options}</select>`;
  }

  /*
    Products with options open the theme's quick add panel (the same popup as
    "Add to cart" on the home page's featured collection). data-qap-sheet
    makes it open as a sheet over the drawer on every screen size (quick-add.js);
    the panel's dialog is fetched on first tap.
  */
  #renderQuickAdd(product, firstAvailable) {
    const url = product.url.split('?')[0];
    return `
          <quick-add-panel class="qap" data-product-url="${url}" data-option-count="${product.options.length}" data-section-id="cart-drawer" data-qap-lazy data-qap-sheet>
            <button type="button" class="button button--full-width qap__opener" aria-haspopup="dialog" aria-expanded="false" data-qap-open ${!firstAvailable.available ? 'disabled' : ''}>
              <span>${firstAvailable.available ? 'Add to cart' : 'Sold out'}</span>
            </button>
          </quick-add-panel>`;
  }

  #renderCard(product) {
    const firstAvailable = product.variants.find(v => v.available) ?? product.variants[0];
    const price = this.#formatPrice(firstAvailable.price);
    const img = product.featured_image;
    const hasOptions = this.#hasMultipleVariants(product);
    // Titles can hold quotes (1/2" Drive) and they land in attributes.
    const title = product.title.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

    return `
      <div class="recommendations-card">
        <a class="recommendations-card__media" href="${product.url}">
          ${img ? `<img src="${img}" alt="${title}" width="100" height="100" loading="lazy">` : ''}
        </a>
        <div class="recommendations-card__details">
          <div class="recommendations-card__details-inner">
            <a class="recommendations-card__title" href="${product.url}" title="${title}" aria-label="${title}">${title}</a>
            <p class="recommendations-card__price" data-price-display>${price}</p>
          </div>
          ${hasOptions ? this.#renderQuickAdd(product, firstAvailable) : `
          <button 
            class="button button--full-width"
            data-variant-id="${firstAvailable.id}"
            ${!firstAvailable.available ? 'disabled' : ''}
          >
            <span class="recommendations-card__loading loading__spinner">
              <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"></circle></svg>
            </span>
            <span class="recommendations-card__status">${firstAvailable.available ? 'Add' : 'Sold out'}</span>
          </button>`}
        </div>
      </div>
    `;
  }

  handleVariantChange(e) {
    const select = e.target.closest('.recommendations-card__variants');
    if (!select) return;

    const card = select.closest('.recommendations-card');
    const selectedOption = select.selectedOptions[0];
    const variantId = selectedOption.value;
    const price = selectedOption.dataset.price;
    const available = !selectedOption.disabled;

    card.querySelector('[data-price-display]').textContent = this.#formatPrice(Number(price));

    const btn = card.querySelector('[data-variant-id]');
    btn.dataset.variantId = variantId;
    btn.disabled = !available;
    btn.textContent = available ? 'Add' : 'Sold out';
  }

  async handleAddToCart(e) {
    const btn = e.target.closest('[data-variant-id]');
    if (!btn || btn.disabled) return;


    const variantId = btn.dataset.variantId;
    btn.setAttribute('aria-disabled', 'true');
    btn.classList.add('loading');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cart = document.querySelector('cart-drawer');
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map(s => s.section));
      formData.append('sections_url', window.location.pathname);
    }

    config.body = formData;

    try {
      const res = await fetch(routes.cart_add_url, config);
      const response = await res.json();

      if (response.status) {
        console.warn('[CartDrawerRecommendation] add to cart error:', response.description);
        btn.textContent = 'Error';
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-drawer-recommendation',
        productVariantId: variantId,
        cartData: response,
      });

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);

        if (!elementToReplace) return;
        elementToReplace.innerHTML = this.getSectionInnerHTML(response.sections[section.section], section.selector);
      });

      btn.textContent = 'Added!';
      setTimeout(() => {
        btn.textContent = 'Add';
      }, 2000);

    } catch (err) {
      console.error('[CartDrawerRecommendation]', err);
      btn.textContent = 'Error';
    } finally {
      btn.classList.remove('loading');
      btn.removeAttribute('aria-disabled');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-recommendation', CartDrawerRecommendation);
// Clarity: what shoppers do in the cart drawer. Events are cart_drawer_*, and the
// cart_drawer tag holds the last thing done so recordings can be filtered by it.
// Listeners sit on the <cart-drawer> host, which survives the drawer re-rendering.
// Clicks are read as they bubble, so a tap the theme swallows (the recommendations
// carousel uses a tap on a half-hidden card to slide it in) isn't counted.
(() => {
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  const track = (name) => {
    try {
      window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
      window.clarity('event', `cart_drawer_${name}`);
      window.clarity('set', 'cart_drawer', name);
    } catch (e) {}
  };
  // Most specific first: the recommendation and line links sit inside other matches.
  const ACTIONS = [
    ['button[name="checkout"]', 'checkout'],
    ['.cart-express__trigger', 'express_open'],
    ['.cart-express__wallets, shopify-accelerated-checkout-cart', 'express_wallet'],
    ['cart-remove-button', 'remove'],
    ['.quantity__button[name="plus"]', 'qty_up'],
    ['.quantity__button[name="minus"]', 'qty_down'],
    ['cart-drawer-recommendation [data-variant-id]', 'rec_add'],
    ['.recommendations-navigation__button', 'rec_browse'],
    ['cart-drawer-recommendation a[href*="/products/"]', 'rec_view'],
    ['[data-garage-trigger]', 'add_vehicle'],
    ['a[href*="/products/"]', 'item_view'],
  ];
  let wasOpen = drawer.classList.contains('active');
  let acted = false;

  new MutationObserver(() => {
    const isOpen = drawer.classList.contains('active');
    if (isOpen === wasOpen) return;
    wasOpen = isOpen;
    if (isOpen) {
      acted = false;
      track(drawer.classList.contains('is-empty') ? 'open_empty' : 'open');
    } else {
      track(acted ? 'close' : 'close_no_action');
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['class'] });

  drawer.addEventListener('click', (e) => {
    if (!e.target.closest) return;
    const action = ACTIONS.find(([selector]) => e.target.closest(selector));
    if (!action) return;
    acted = true;
    track(action[1]);
  });

  drawer.addEventListener('change', (e) => {
    if (!e.target.matches) return;
    // The +/- buttons fire a synthetic change on the input too; only count real typing.
    if (e.target.matches('.quantity__input') && e.isTrusted) {
      acted = true;
      track('qty_typed');
    } else if (e.target.matches('.footer-upsell__price-switch-input')) {
      acted = true;
      track(e.target.checked ? 'protection_on' : 'protection_off');
    }
  }, true);
})();
